import React, { useState } from 'react';
import { CheckCircle2, Circle, AlertTriangle, Clock, ChevronDown, ChevronUp } from 'lucide-react';

interface Fase {
  id: string; nombre: string; estado: 'completada' | 'en_progreso' | 'pendiente';
  pct: number; tareas: Tarea[];
}
interface Tarea {
  id: string; texto: string; responsable: string;
  estado: 'done' | 'doing' | 'todo'; prioridad: 'alta' | 'media' | 'baja';
}

const DEMO: Array<{ obra: string; fases: Fase[] }> = [
  {
    obra: 'Construcción Chalet C/ Goya, Sevilla',
    fases: [
      { id: 'f1', nombre: 'Cimentación', estado: 'completada', pct: 100, tareas: [
        { id: 't1', texto: 'Replanteo y excavación', responsable: 'DV', estado: 'done', prioridad: 'alta' },
        { id: 't2', texto: 'Armado y encofrado', responsable: 'CR', estado: 'done', prioridad: 'alta' },
        { id: 't3', texto: 'Hormigonado y curado', responsable: 'DV', estado: 'done', prioridad: 'alta' },
      ]},
      { id: 'f2', nombre: 'Estructura', estado: 'en_progreso', pct: 60, tareas: [
        { id: 't4', texto: 'Pilares planta baja', responsable: 'DV', estado: 'done', prioridad: 'alta' },
        { id: 't5', texto: 'Forjado planta primera', responsable: 'CR', estado: 'doing', prioridad: 'alta' },
        { id: 't6', texto: 'Cubierta inclinada', responsable: 'MG', estado: 'todo', prioridad: 'media' },
      ]},
      { id: 'f3', nombre: 'Cerramientos', estado: 'pendiente', pct: 0, tareas: [
        { id: 't7', texto: 'Fachada ladrillo cara vista', responsable: 'AL', estado: 'todo', prioridad: 'alta' },
        { id: 't8', texto: 'Ventanas PVC triple vidrio', responsable: 'PS', estado: 'todo', prioridad: 'media' },
      ]},
      { id: 'f4', nombre: 'Instalaciones', estado: 'pendiente', pct: 0, tareas: [
        { id: 't9', texto: 'Fontanería', responsable: 'AL', estado: 'todo', prioridad: 'alta' },
        { id: 't10', texto: 'Electricidad BT', responsable: 'AL', estado: 'todo', prioridad: 'alta' },
        { id: 't11', texto: 'Climatización', responsable: 'TM', estado: 'todo', prioridad: 'media' },
      ]},
      { id: 'f5', nombre: 'Acabados', estado: 'pendiente', pct: 0, tareas: [
        { id: 't12', texto: 'Pintura interior', responsable: 'CR', estado: 'todo', prioridad: 'baja' },
        { id: 't13', texto: 'Pavimento', responsable: 'RN', estado: 'todo', prioridad: 'baja' },
      ]},
    ],
  },
];

const estadoFaseColor: Record<string, string> = {
  completada: 'bg-green-500',
  en_progreso: 'bg-orange-400',
  pendiente: 'bg-gray-300',
};

const prioColor: Record<string, string> = {
  alta: 'bg-red-400', media: 'bg-orange-400', baja: 'bg-blue-400',
};

export default function SeguimientoPage() {
  const [open, setOpen] = useState<string>('f2');
  const obra = DEMO[0];
  const totalTareas = obra.fases.flatMap(f => f.tareas).length;
  const doneTareas = obra.fases.flatMap(f => f.tareas).filter(t => t.estado === 'done').length;
  const pctGlobal = Math.round((doneTareas / totalTareas) * 100);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-1">Seguimiento de Obra</h1>
      <p className="text-sm text-gray-500 mb-6">{obra.obra}</p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Progreso global</div>
          <div className="text-3xl font-bold">{pctGlobal}%</div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-orange-400 rounded-full" style={{ width: `${pctGlobal}%` }} />
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fases completadas</div>
          <div className="text-3xl font-bold">{obra.fases.filter(f => f.estado === 'completada').length}/{obra.fases.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tareas completadas</div>
          <div className="text-3xl font-bold">{doneTareas}/{totalTareas}</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex items-center gap-0 mb-8 overflow-x-auto pb-2">
        {obra.fases.map((f, i) => (
          <React.Fragment key={f.id}>
            <div className="flex flex-col items-center min-w-[110px]">
              <div className={`w-8 h-8 rounded-full ${estadoFaseColor[f.estado]} flex items-center justify-center text-white text-xs font-bold z-10`}>
                {f.estado === 'completada' ? '✓' : i + 1}
              </div>
              <div className="text-xs text-center mt-1 text-gray-500 w-24 leading-tight">{f.nombre}</div>
              <div className="text-xs text-center text-gray-400">{f.pct}%</div>
            </div>
            {i < obra.fases.length - 1 && (
              <div className="flex-1 h-0.5 bg-gray-200 min-w-[20px] mt-[-20px]" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Fases accordion */}
      <div className="space-y-3">
        {obra.fases.map(f => (
          <div key={f.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <button
              onClick={() => setOpen(open === f.id ? '' : f.id)}
              className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors"
            >
              <div className={`w-3 h-3 rounded-full ${estadoFaseColor[f.estado]}`} />
              <span className="flex-1 text-left font-semibold text-gray-900">{f.nombre}</span>
              <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${estadoFaseColor[f.estado]}`} style={{ width: `${f.pct}%` }} />
              </div>
              <span className="text-xs text-gray-400 w-10 text-right">{f.pct}%</span>
              {open === f.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>
            {open === f.id && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="space-y-2 mt-3">
                  {f.tareas.map(t => (
                    <div key={t.id} className="flex items-center gap-3">
                      {t.estado === 'done' ? <CheckCircle2 size={16} className="text-green-400 flex-shrink-0" /> :
                        t.estado === 'doing' ? <Clock size={16} className="text-orange-400 flex-shrink-0 animate-pulse" /> :
                        <Circle size={16} className="text-gray-300 flex-shrink-0" />}
                      <span className={`flex-1 text-sm ${t.estado === 'done' ? 'line-through text-gray-400' : 'text-gray-700'}`}>{t.texto}</span>
                      <div className={`w-2.5 h-2.5 rounded-full ${prioColor[t.prioridad]}`} title={t.prioridad} />
                      <span className="text-xs font-mono text-gray-400 w-8 text-right">{t.responsable}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
